import React, { useEffect } from 'react';
import { useScrollLock } from '../../_utils/useScrollLock';
import ReactDOM from 'react-dom';
import CloseButton from '../shared/CloseButton';
import { useHistory } from 'react-router-dom';
import VoucherCard from '../gift-cards-v2/VoucherCard';
import { fmtExpiry, resolveWalletDesign } from './WalletCard';
import '../gift-cards-v2/GiftCardsV2.css';

// WalletCardDetail — a slide-up sheet (mobile) / centred card (desktop) showing
// the full card, code, balance, expiry, and the key action: use it at the salon
// (deep-links to that salon's booking page).
const WalletCardDetail = ({ item, onClose, onUse, onChooseDesign }) => {
  const history = useHistory();
  const { voucher, aesthetic, undesigned } = resolveWalletDesign(item);

  // Shared ref-counted lock: overlapping modals must not restore each
  // other's locked styles (see SalonProfilePage/ConfirmBookingModal).
  useScrollLock(true);

  const expiry = fmtExpiry(item.expiresAt);
  const partlySpent = item.faceValue !== item.balance;

  const useAtSalon = () => {
    onClose && onClose();
    // On the salon page, open the booking flow directly (the customer is already
    // here). Elsewhere (the wallet), navigate to the salon's booking page.
    if (onUse) { onUse(item); return; }
    history.push(`/book/${item.salon.slug || item.salonId}`);
  };

  // Render through a portal to <body> so the sheet escapes any ancestor stacking
  // context (e.g. a transformed wrapper on the salon page) and truly overlays
  // the page rather than letting later content paint through it.
  return ReactDOM.createPortal(
    <div className="wallet-modal-overlay" onClick={onClose}>
      <div className="wallet-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Gift card details">
        <header className="wallet-modal-header">
          <h2 className="wallet-modal-title">{item.salon.name}</h2>
          <CloseButton onClick={onClose} />
        </header>

        <div className="wallet-modal-body">
          {undesigned ? (
            <div className="wallet-detail-art wallet-undesigned">
              <span className="wallet-undesigned-icon" aria-hidden>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.1 6.3a3 3 0 0 0 1.6 1.6L22 12l-6.3 2.1a3 3 0 0 0-1.6 1.6L12 22l-2.1-6.3a3 3 0 0 0-1.6-1.6L2 12l6.3-2.1a3 3 0 0 0 1.6-1.6L12 2z" /></svg>
              </span>
              <span className="wallet-undesigned-title">No design chosen yet</span>
              <span className="wallet-undesigned-sub">Personalise it below</span>
            </div>
          ) : (
            <div className="voucher-v2 wallet-detail-art">
              <VoucherCard
                voucher={voucher}
                aesthetic={aesthetic}
                salonName={item.salon.name}
                currency={item.currency}
                showPromo={false}
              />
            </div>
          )}

          <div className="wallet-detail-rows">
            <div className="wallet-detail-row">
              <span>Balance</span>
              <strong>{item.currency}{item.balance}</strong>
            </div>
            {partlySpent && (
              <div className="wallet-detail-row">
                <span>Original value</span>
                <strong>{item.currency}{item.faceValue}</strong>
              </div>
            )}
            <div className="wallet-detail-row">
              <span>Code</span>
              <strong className="wallet-detail-code">{item.code}</strong>
            </div>
            {expiry && (
              <div className="wallet-detail-row">
                <span>Expires</span>
                <strong>{expiry}</strong>
              </div>
            )}
          </div>
        </div>

        <div className="wallet-modal-footer">
          {undesigned ? (
            <button type="button" className="wallet-cta" onClick={() => onChooseDesign && onChooseDesign(item)}>
              Choose a design
            </button>
          ) : (
            <button type="button" className="wallet-cta" onClick={useAtSalon}>
              Use at {item.salon.name}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WalletCardDetail;
